import { FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  Action,
  AssistantInterface,
  AssistantType,
  ConnectionInterface,
  Effect,
  GoalType,
  NamespaceInterface,
  PromptModuleApiService,
  SituationConfigInterface,
  ValidatorInterface,
} from '@vendasta/ai-assistants';
import { KnowledgeSource } from '@vendasta/embeddings';
import {
  DEFAULT_ASSISTANT_NAMES,
  DEFAULT_CUSTOM_PERSONALITY_GOAL_ID,
  VoiceProviderFeatureConfig,
} from '../ai-assistant.constants';
import { AiAssistant } from '../interfaces/ai-assistant.interface';
import { globalNamespace } from '../services/ai-assistant-utils';
import { AiToolFormRegistryService } from '../services/ai-tool-form-registry.service';
import { AiCapabilityFormArray } from './ai-capability.form';
import { AiConnectionsForm } from './ai-connections.form';
import { AiKnowledgeFormArray } from './ai-knowledge.form';
import { AiModelConfigurationForm } from './ai-model-config.form';
import { AiSituationConfigForm } from './ai-situation-config.form';
import { AiValidatorsFormArray } from './ai-validators.form';
import { AiVoiceConfigurationForm } from './ai-voice-model-config.form';

// Assistant
export type AiAssistantFormControls = {
  id: FormControl<string | null>;
  namespace: FormControl<NamespaceInterface | null>;
  name: FormControl<string | null>;
  role: FormControl<string | null>;
  description: FormControl<string | null>;
  avatarUrl: FormControl<string | null>;
  capabilities: AiCapabilityFormArray;
  connections: AiConnectionsForm;
  knowledge: AiKnowledgeFormArray;
  voiceConfig: AiVoiceConfigurationForm;
  situationConfig: AiSituationConfigForm;
  type: FormControl<AssistantType | null>;
  model: AiModelConfigurationForm;
  inputValidators: AiValidatorsFormArray;
  outputValidators: AiValidatorsFormArray;
};

export class AiAssistantForm extends FormGroup<AiAssistantFormControls> {
  aiAssistant: AiAssistant | undefined;

  constructor(
    promptModuleApiService: PromptModuleApiService,
    assistant?: AiAssistant,
    promptModuleContents?: Record<string, string>,
    knowledgeSources?: KnowledgeSource[],
    toolFormRegistry?: AiToolFormRegistryService,
    voiceFeatures?: VoiceProviderFeatureConfig,
    translateService?: TranslateService,
  ) {
    const type = assistant?.assistant.type ?? AssistantType.ASSISTANT_TYPE_CUSTOM;
    let goals = assistant?.assistant.configurableGoals?.filter((goal) => goal !== undefined) || [];
    const model = assistant?.assistant?.config?.models?.[0];

    // Ensure there's always at least one personality goal per assistant.
    if (!goals.some((goal) => goal.goal?.type === GoalType.GOAL_TYPE_PERSONALITY)) {
      const isCustomAssistant = type === AssistantType.ASSISTANT_TYPE_CUSTOM;

      goals = [
        {
          goal: {
            type: GoalType.GOAL_TYPE_PERSONALITY,
            name: `${assistant?.assistant.name || 'Assistant'} Personality`,
            namespace: assistant?.assistant.namespace,
            promptModules: [
              {
                namespace: assistant?.assistant.namespace,
              },
            ],
            // All personality capabilities should be hidden
            constraints: [
              {
                action: Action.ACTION_VIEW,
                effect: Effect.EFFECT_DENY,
              },
            ],
            // Set the override to the default custom personality if the assistant is custom
            ...(isCustomAssistant && {
              overrideOf: {
                id: DEFAULT_CUSTOM_PERSONALITY_GOAL_ID,
                namespace: globalNamespace,
              },
            }),
          },
        },
        ...goals,
      ];
    }

    const defaultRole = translateService?.instant('AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.CUSTOM_ASSISTANT.TITLE');
    const defaultDescription = translateService?.instant(
      'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.CUSTOM_ASSISTANT.DESCRIPTION',
    );

    super({
      id: new FormControl({ value: assistant?.assistant.id ?? null, disabled: true }),
      type: new FormControl({ value: type, disabled: true }),
      namespace: new FormControl(assistant?.assistant.namespace ?? null),
      name: new FormControl(assistant?.assistant.name || ''),
      role: new FormControl(assistant?.assistant.role || defaultRole),
      description: new FormControl(assistant?.assistant.description || defaultDescription),
      avatarUrl: new FormControl(assistant?.assistant.avatarUrl || ''),
      capabilities: new AiCapabilityFormArray(
        promptModuleApiService,
        goals,
        promptModuleContents || {},
        toolFormRegistry,
      ),
      connections: new AiConnectionsForm(assistant?.connections || [], assistant?.assistant.id || ''),
      knowledge: new AiKnowledgeFormArray(knowledgeSources ?? []),
      voiceConfig: new AiVoiceConfigurationForm(assistant?.assistant?.config?.voiceConfig, voiceFeatures),
      situationConfig: new AiSituationConfigForm({
        situationMappings:
          assistant?.assistant?.config?.situationConfig?.simpleConfig?.map((mapping) => ({
            situation: mapping.situation,
            goalKeys: mapping.goalKeys ?? [],
          })) ?? [],
      }),
      model: new AiModelConfigurationForm(model),
      inputValidators: new AiValidatorsFormArray(
        assistant?.assistant.inputValidators as ValidatorInterface[] | undefined,
      ),
      outputValidators: new AiValidatorsFormArray(
        assistant?.assistant.outputValidators as ValidatorInterface[] | undefined,
      ),
    });

    if (assistant?.assistant?.namespace?.systemNamespace) {
      this.controls.capabilities.controls.forEach((cap) => {
        cap.controls.name.disable();
        cap.controls.description.disable();
        cap.controls.promptModules.disable();
        cap.controls.tools.controls.forEach((tool) => tool.disable());
      });
    }

    this.aiAssistant = assistant;
  }

  toAssistant(localNamespace: NamespaceInterface): AssistantInterface {
    const isVoiceAssistant = this.controls.type.getRawValue() === AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST;
    const model = this.controls.model.toAssistantModel();

    const defaultName = DEFAULT_ASSISTANT_NAMES.get(
      this.controls.type.getRawValue() ?? AssistantType.ASSISTANT_TYPE_CUSTOM,
    );

    const role = this.controls.role.dirty
      ? (this.controls.role.getRawValue() ?? undefined)
      : this.aiAssistant?.assistant.role;
    const description = this.controls.description.dirty
      ? (this.controls.description.getRawValue() ?? undefined)
      : this.aiAssistant?.assistant.description;

    // Get situation config - include if dirty, otherwise preserve existing
    const situationConfig: SituationConfigInterface | undefined = this.controls.situationConfig.dirty
      ? this.controls.situationConfig.toSituationConfig()
      : this.aiAssistant?.assistant.config?.situationConfig;

    const baseConfig = isVoiceAssistant
      ? {
          voiceConfig: this.controls.voiceConfig.toAssistantVoiceConfig(),
        }
      : {
          models: model ? [model] : [],
        };

    const inputValidators = this.controls.inputValidators.toValidators();
    const outputValidators = this.controls.outputValidators.toValidators();

    const automationBlueprints = this.aiAssistant?.assistant.automationBlueprints;

    const result: AssistantInterface = {
      id: this.controls.id.getRawValue() ?? undefined,
      type: this.controls.type.getRawValue() ?? AssistantType.ASSISTANT_TYPE_CUSTOM,
      namespace: this.controls.namespace.getRawValue() ?? localNamespace,
      name: this.controls.name.getRawValue() || defaultName,
      role: role,
      description: description,
      avatarUrl: this.controls.avatarUrl.getRawValue() ?? undefined,
      configurableGoals: this.controls.capabilities.toConfigurableGoals(localNamespace),
      config: {
        ...baseConfig,
        ...(situationConfig ? { situationConfig } : {}),
      },
      inputValidators: inputValidators,
      outputValidators: outputValidators,
      ...(automationBlueprints?.length ? { automationBlueprints } : {}),
    };

    return result;
  }

  toConnections(): ConnectionInterface[] {
    return this.controls.connections.toConnections();
  }
}
